import React from 'react';
import { Navigate } from 'react-router-dom';
import { ExclamationTriangleIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import useAutorizzazioni from '../hooks/useAutorizzazioni';

const ProtectedRoute = ({ 
  children, 
  user, 
  requiredPage, 
  requiredSubpage = null,
  fallbackPath = '/nlt/',
  showAccessDenied = true 
}) => {
  const { 
    verificaAccesso, 
    loading, 
    error, 
    ruoloMappato 
  } = useAutorizzazioni(user);

  // Mostra loading durante il caricamento delle autorizzazioni
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifica autorizzazioni in corso...</p>
        </div>
      </div>
    );
  }

  // Se c'è un errore nel caricamento autorizzazioni, mostra errore
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Errore Autorizzazioni
          </h2>
          <p className="text-gray-600 mb-4">
            Non è stato possibile verificare le tue autorizzazioni.
          </p>
          <p className="text-sm text-red-600 mb-4">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  // Verifica l'accesso alla risorsa richiesta
  const hasAccess = verificaAccesso(requiredPage, requiredSubpage);

  // Se non ha accesso, mostra pagina di accesso negato o reindirizza
  if (!hasAccess) {
    if (showAccessDenied) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <ShieldExclamationIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Accesso Negato
            </h2>
            <p className="text-gray-600 mb-4">
              Non hai i permessi necessari per accedere a questa pagina.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="text-sm text-gray-700">
                <p><strong>Il tuo ruolo:</strong> {user?.ruolo} ({ruoloMappato})</p>
                <p><strong>Pagina richiesta:</strong> {requiredPage}</p>
                {requiredSubpage && (
                  <p><strong>Sottopagina:</strong> {requiredSubpage}</p>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Contatta l'amministratore se ritieni di dover avere accesso a questa risorsa.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors mr-2"
            >
              Torna Indietro
            </button>
            <button
              onClick={() => window.location.href = fallbackPath}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Vai alla Dashboard
            </button>
          </div>
        </div>
      );
    } else {
      // Reindirizza silenziosamente
      return <Navigate to={fallbackPath} replace />;
    }
  }

  // Se ha accesso, mostra il contenuto
  return children;
};

export default ProtectedRoute; 